/** @format */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page } from "react-pdf";

import { PdfControls } from "./PdfControls";
import "./setup";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import type { PdfFileMinimal } from "./types";
import { FileText } from "lucide-react";

export interface PdfViewerProps<T extends PdfFileMinimal = PdfFileMinimal> {
  files: T[];
  className?: string;
  showControls?: boolean;
  emptyStateMessage?: {
    title: string;
    description: string;
  };
  initialScale?: number;
  onDownload?: () => void;
}

const PdfViewer = <T extends PdfFileMinimal = PdfFileMinimal>({
  files,
  className = "",
  showControls = true,
  emptyStateMessage = {
    title: "No PDF files found",
    description: "No PDF documents are available",
  },
  initialScale = 0.5,
  onDownload,
}: PdfViewerProps<T>) => {
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(initialScale);
  const [rotation, setRotation] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const didDragRef = useRef(false);
  const dragStateRef = useRef({
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  const pdfFiles = files.filter(
    (file) =>
      file.fileName.toLowerCase().endsWith(".pdf") && file.signedUrl != null,
  );
  const currentFile = useMemo(
    () => pdfFiles[currentFileIndex],
    [pdfFiles, currentFileIndex],
  );
  const currentFileUrl = currentFile?.signedUrl ?? null;

  const fileConfig = useMemo(
    () => (currentFileUrl ? { url: currentFileUrl } : null),
    [currentFileUrl],
  );

  useEffect(() => {
    if (currentFileIndex >= pdfFiles.length && pdfFiles.length > 0) {
      setCurrentFileIndex(0);
    }
  }, [currentFileIndex, pdfFiles.length]);

  useEffect(() => {
    setNumPages(null);
    setError(null);
    pageRefs.current = {};
  }, [currentFileUrl]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: loadedNumPages }: { numPages: number }) => {
      setNumPages(loadedNumPages);
      setError(null);
    },
    [],
  );

  const onDocumentLoadError = useCallback((err: Error) => {
    if (
      err.message?.includes("messageHandler") ||
      err.message?.includes("sendWithPromise")
    ) {
      return;
    }
    setError("Failed to load PDF document");
  }, []);

  const onPageLoadSuccess = useCallback(() => {
    // Optional: could scroll to page or track loaded pages here
  }, []);

  const onPageLoadError = useCallback((err: Error) => {
    if (
      err.message?.includes("messageHandler") ||
      err.message?.includes("sendWithPromise")
    ) {
      return;
    }
    console.error("PDF page load error:", err);
  }, []);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3.0));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.25));
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  const toggleFullscreen = useCallback(() => {
    if (!fullscreenContainerRef.current) return;
    if (!document.fullscreenElement) {
      fullscreenContainerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () =>
      setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current;
    if (!container || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    didDragRef.current = false;
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
    isDraggingRef.current = true;
    container.style.userSelect = "none";
    container.style.cursor = "grabbing";
    container.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    event.preventDefault();
    event.stopPropagation();
    didDragRef.current = true;
    const { startX, startY, scrollLeft, scrollTop } = dragStateRef.current;
    container.scrollLeft = scrollLeft - (event.clientX - startX);
    container.scrollTop = scrollTop - (event.clientY - startY);
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    const container = scrollContainerRef.current;
    if (container?.hasPointerCapture?.(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
    event.preventDefault();
    event.stopPropagation();
    isDraggingRef.current = false;
    if (container) {
      container.style.removeProperty("user-select");
      container.style.cursor = "grab";
    }
  };

  const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (didDragRef.current) {
      event.preventDefault();
      event.stopPropagation();
      didDragRef.current = false;
    }
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`flex min-w-0 flex-1 flex-col bg-slate-900/40 ${className} ${isFullscreen ? "bg-slate-900" : ""}`}
    >
      {showControls && (
        <PdfControls
          numPages={numPages}
          scale={scale}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onRotate={handleRotate}
          onDownload={onDownload}
          onFullscreenToggle={toggleFullscreen}
          isFullscreen={isFullscreen}
        />
      )}

      <div
        ref={scrollContainerRef}
        className="flex-1 min-w-0 overflow-auto p-4 pt-0 select-none cursor-grab"
        style={{ touchAction: "none" }}
        title="Drag to move the document"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={handleClickCapture}
      >
        <div className="min-w-full w-max flex justify-center">
          {currentFileUrl ? (
            <div className="bg-white shadow-lg rounded overflow-hidden shrink-0">
              {error ? (
                <div className="flex flex-col items-center justify-center p-8 gap-3">
                  <FileText className="h-12 w-12 text-slate-400" />
                  <p className="text-slate-600 font-medium">
                    Error loading PDF
                  </p>
                  <p className="text-sm text-slate-500 text-center">{error}</p>
                  <a
                    href={currentFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-slate-600 hover:text-slate-800 underline"
                  >
                    Open in new tab
                  </a>
                </div>
              ) : (
                <Document
                  key={currentFileUrl}
                  file={fileConfig}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={
                    <div className="flex items-center justify-center p-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-300 border-t-slate-600" />
                      <span className="ml-2 text-slate-600">
                        Loading PDF...
                      </span>
                    </div>
                  }
                >
                  {numPages != null &&
                    Array.from(new Array(numPages), (_, index) => (
                      <div
                        key={`page_${index + 1}`}
                        className="mb-4 relative block"
                        ref={(el) => {
                          pageRefs.current[index + 1] = el;
                        }}
                      >
                        <Page
                          pageNumber={index + 1}
                          scale={scale}
                          rotate={rotation}
                          onLoadSuccess={onPageLoadSuccess}
                          onLoadError={onPageLoadError}
                          loading={
                            <div className="flex items-center justify-center p-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-300 border-t-slate-600" />
                            </div>
                          }
                        />
                      </div>
                    ))}
                </Document>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 w-full">
              <FileText className="h-12 w-12 text-slate-400 mb-4" />
              <p className="text-slate-600 mb-2">{emptyStateMessage.title}</p>
              <p className="text-sm text-slate-500">
                {emptyStateMessage.description}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { PdfViewer };
